'use client';

import { createContext, useContext, ReactNode, useMemo, useEffect } from 'react';
import { usePolling } from '@/hooks/usePolling';
import { getStoreLocations } from '@/lib/api/location';
import { StoreLocation } from '@/lib/api/location';
import { useAuth } from './AuthContext';
import { useInstacartAuth } from './InstacartAuthContext';
import { useUserLocation } from './UserLocationContext';

interface StoreLocationsContextType {
  stores: StoreLocation[];
  isLoading: boolean;
  error: Error | null;
}

const StoreLocationsContext = createContext<StoreLocationsContextType | undefined>(undefined);

export function StoreLocationsProvider({ children }: { children: ReactNode }) {
  const { sessionId } = useAuth();
  const { isInstacartLinked } = useInstacartAuth();
  const { userCoords } = useUserLocation();
  const { data: stores = [], isLoading, error } = usePolling({
    key: 'store-locations',
    fetchFn: async () => {
      if (!sessionId) {
        console.log('Polling disabled due to missing session.');
        return [];
      }

      if (!isInstacartLinked) {
        console.log('Polling disabled due to unlinked Instacart account.');
        return [];
      }

      if (!userCoords) {
        console.log('No user coordinates available.');
        return [];
      }

      const { latitude, longitude } = userCoords;
      const response = await getStoreLocations(sessionId, latitude, longitude);
      console.log('Store locations response:', response);
      console.log('latitude:', latitude);
      console.log('longitude:', longitude);
      // console.log('Store locations response:', response);
      if (response.statusCode === 200 && response.body?.stores) {
        // console.log('Fetched new store locations:', response.body.stores);
        return response.body.stores;
      }
      console.log('Failed to fetch store locations or no stores found.');
      return [];
    },
    interval: 60000, // Poll every minute
    enabled: !!sessionId && isInstacartLinked && !!userCoords,
    showToast: false,
  });

  // Just memoize stores as-is, do not add a key property
  const memoizedStores = useMemo(() => stores, [stores]);

  useEffect(() => {
    console.log('Memoized stores changed, causing re-render:', memoizedStores);
  }, [memoizedStores]);

  return (
    <StoreLocationsContext.Provider value={{ stores: memoizedStores, isLoading, error }}>
      {children}
    </StoreLocationsContext.Provider>
  );
}

export function useStoreLocations() {
  const context = useContext(StoreLocationsContext);
  if (context === undefined) {
    throw new Error('useStoreLocations must be used within a StoreLocationsProvider');
  }
  return context;
} 